import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  ChevronLeft, ChevronRight, Play, Pause, Monitor, Clock, 
  FileText, Eye, Maximize2, Volume2, Radio, Smartphone, MoonStar
} from 'lucide-react';
import TeleprompterView from '@/components/TeleprompterView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PresenterView = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const isPopout = searchParams.get('popout') === 'true';
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [teleprompter, setTeleprompter] = useState(() => (
    typeof window !== 'undefined' && localStorage.getItem('presenter_teleprompter') === '1'
  ));
  
  const timerRef = useRef(null);
  const broadcastChannel = useRef(null);

  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Setup BroadcastChannel for syncing with main presentation window
  useEffect(() => {
    broadcastChannel.current = new BroadcastChannel(`slidecon-${projectId}`);
    
    broadcastChannel.current.onmessage = (event) => {
      if (event.data.type === 'SLIDE_CHANGE') {
        setCurrentIndex(event.data.index);
      }
      if (event.data.type === 'STREAM_STATUS') {
        setIsStreaming(event.data.streaming);
      }
    };

    return () => {
      broadcastChannel.current?.close();
    };
  }, [projectId]);

  // Send slide changes to main presentation
  const broadcastSlideChange = (index) => {
    broadcastChannel.current?.postMessage({ type: 'SLIDE_CHANGE', index });
  };

  // Elapsed time timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Auto-play
  useEffect(() => {
    if (!autoPlay || !project) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        const next = prev >= project.slides.length - 1 ? 0 : prev + 1;
        broadcastSlideChange(next);
        return next;
      });
    }, (project.auto_scroll_speed || 5) * 1000);

    return () => clearInterval(interval);
  }, [autoPlay, project]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToSlide(currentIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToSlide(currentIndex - 1);
      } else if (e.key === 'p' || e.key === 'P') {
        setAutoPlay(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, project?.slides?.length]);

  const goToSlide = (index) => {
    if (!project) return;
    const newIndex = Math.max(0, Math.min(project.slides.length - 1, index));
    setCurrentIndex(newIndex);
    broadcastSlideChange(newIndex);
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const openAudienceView = () => {
    window.open(`/present/${projectId}`, 'audience-view', 'width=1920,height=1080');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <p>Project not found</p>
      </div>
    );
  }

  const currentSlide = project.slides[currentIndex];
  const nextSlide = project.slides[currentIndex + 1];
  const prevSlide = project.slides[currentIndex - 1];

  if (teleprompter) {
    return (
      <TeleprompterView
        project={project}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        elapsedTime={elapsedTime}
        onExit={() => {
          localStorage.setItem('presenter_teleprompter', '0');
          setTeleprompter(false);
        }}
        broadcastChannel={broadcastChannel}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-amber-500" />
            <h1 className="font-bold text-lg">{project.name}</h1>
          </div>
          <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded">
            PRESENTER VIEW
          </span>
          {isStreaming && (
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded flex items-center gap-1">
              <Radio className="w-3 h-3 animate-pulse" />
              LIVE
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-slate-400">
            <Clock className="w-4 h-4" />
            <span className="font-mono">{formatTime(elapsedTime)}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              localStorage.setItem('presenter_teleprompter', '1');
              setTeleprompter(true);
            }}
            className="gap-2"
            data-testid="teleprompter-mode-btn"
          >
            <Smartphone className="w-4 h-4" />
            Teleprompter
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openAudienceView}
            className="gap-2"
          >
            <Maximize2 className="w-4 h-4" />
            Open Audience View
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-100px)]">
        {/* Current Slide - Large Preview */}
        <div className="col-span-7 flex flex-col">
          <div className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Current Slide</div>
          <div 
            className="flex-1 rounded-xl overflow-hidden relative"
            style={{ 
              backgroundColor: currentSlide?.background_color || '#1a1a2e',
              ...(currentSlide?.background_type === 'gradient' && currentSlide?.background_image && {
                background: currentSlide.background_image
              })
            }}
          >
            {/* Video Background */}
            {currentSlide?.background_type === 'video' && currentSlide?.background_video && (
              <video 
                src={currentSlide.background_video}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
              />
            )}
            {/* Image Background */}
            {currentSlide?.background_type === 'image' && currentSlide?.background_image && (
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${currentSlide.background_image})` }}
              />
            )}
            {/* Overlay */}
            {(currentSlide?.background_type === 'image' || currentSlide?.background_type === 'video') && (
              <div className="absolute inset-0 bg-black/30" />
            )}
            
            {/* Content */}
            <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
              <div className="relative z-10">
                {currentSlide?.type && currentSlide.type !== 'text' && currentSlide.type !== 'title' && (
                  <div className="mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      currentSlide.type === 'chorus' ? 'bg-amber-500 text-black' : 'bg-white/20'
                    }`}>
                      {currentSlide.type.toUpperCase()}
                    </span>
                  </div>
                )}
                <p 
                  className="font-lyrics leading-relaxed whitespace-pre-line"
                  style={{ 
                    color: currentSlide?.text_color || '#ffffff',
                    fontSize: `clamp(18px, 3vw, 48px)`
                  }}
                >
                  {currentSlide?.content}
                </p>
              </div>
            </div>

            {/* Slide number badge */}
            <div className="absolute top-3 left-3 px-2 py-1 bg-black/50 rounded text-sm">
              {currentIndex + 1} / {project.slides.length}
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="col-span-5 flex flex-col gap-4">
          {/* Next Slide Preview */}
          <div className="flex-1">
            <div className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Next Slide</div>
            <div 
              className="h-full rounded-xl overflow-hidden relative opacity-70"
              style={{ 
                backgroundColor: nextSlide?.background_color || '#2a2a3e',
                ...(nextSlide?.background_type === 'gradient' && nextSlide?.background_image && {
                  background: nextSlide.background_image
                })
              }}
            >
              {nextSlide ? (
                <>
                  {nextSlide.background_type === 'image' && nextSlide.background_image && (
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${nextSlide.background_image})` }}
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center p-4 text-center">
                    <p 
                      className="font-lyrics text-sm leading-relaxed whitespace-pre-line line-clamp-6"
                      style={{ color: nextSlide.text_color || '#ffffff' }}
                    >
                      {nextSlide.content}
                    </p>
                  </div>
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 rounded text-xs">
                    {currentIndex + 2}
                  </div>
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-600">
                  <p>End of presentation</p>
                </div>
              )}
            </div>
          </div>

          {/* Speaker Notes */}
          <div className="flex-1 bg-slate-900 rounded-xl p-4 overflow-hidden">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 uppercase tracking-wider">
              <FileText className="w-4 h-4" />
              Speaker Notes
            </div>
            <div className="h-[calc(100%-30px)] overflow-y-auto">
              {currentSlide?.notes ? (
                <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                  {currentSlide.notes}
                </p>
              ) : (
                <p className="text-slate-600 italic">No notes for this slide</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-800 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Slide Thumbnails */}
          <div className="flex items-center gap-2 overflow-x-auto max-w-[60%] pb-2">
            {project.slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => goToSlide(index)}
                className={`flex-shrink-0 w-20 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-amber-500 ring-2 ring-amber-500/30' 
                    : 'border-transparent hover:border-slate-600'
                }`}
                style={{ backgroundColor: slide.background_color }}
              >
                <div className="w-full h-full flex items-center justify-center p-1">
                  <p className="text-[8px] text-white line-clamp-2 text-center">
                    {slide.content?.substring(0, 30)}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => goToSlide(currentIndex - 1)}
              disabled={currentIndex === 0}
              className="w-12 h-12"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            
            <Button
              variant={autoPlay ? 'default' : 'outline'}
              size="icon"
              onClick={() => setAutoPlay(!autoPlay)}
              className={`w-12 h-12 ${autoPlay ? 'bg-amber-500 hover:bg-amber-600 text-black' : ''}`}
            >
              {autoPlay ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={() => goToSlide(currentIndex + 1)}
              disabled={currentIndex === project.slides.length - 1}
              className="w-12 h-12"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresenterView;
